import React, { Dispatch, FC, SetStateAction } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { Formik, Form } from 'formik';
import { deepEqual } from 'fast-equals';
import { Save, Close } from '@mui/icons-material';
// Components

import {
  Card as CardMui,
  Fade,
  CardActions,
  Button,
  CardContent,
  CardHeader,
  Grid,
  Paper,
  Typography,
  List
} from '@mui/material';
// models

// fetch

import { useSnackbar } from 'notistack';

import { Loader, Modal } from '../../../../../components';
import { IBudgetOption, ISelectedBudgetOption } from '../../../../../models';
import { BudgetOptionsField } from './budget-options-field';
import { updateNeighborhoodSeriesSelectedBudgetOptions } from '../../../../../fetch';

interface ISeriesBudgetOptionsModal {
  open: boolean;
  modalIsLoading: boolean;
  onClose: (cancelled) => void;
  budgetOptions: IBudgetOption[] | null;
  selectedBudgetOptions: ISelectedBudgetOption[] | null;
  originalSelectedBudgetOptions: ISelectedBudgetOption[] | null;
  setSelectedBudgetOptions: Dispatch<SetStateAction<ISelectedBudgetOption[]>>;
  fetchBudgetOptions: () => void;
  selectedSeriesId?: string | number;
  neighborhoodId: string | number;
}

export const SeriesBudgetOptionsModal: FC<ISeriesBudgetOptionsModal> = ({
  open,
  modalIsLoading,
  onClose,
  budgetOptions,
  selectedBudgetOptions,
  originalSelectedBudgetOptions,
  setSelectedBudgetOptions,
  fetchBudgetOptions,
  selectedSeriesId,
  neighborhoodId
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{}}
        onSubmit={async (values, actions) => {
          try {
            await updateNeighborhoodSeriesSelectedBudgetOptions(
              neighborhoodId,
              selectedSeriesId,
              selectedBudgetOptions
            );
            enqueueSnackbar(`Budget Options updated successfully!`, {
              variant: 'success'
            });
            fetchBudgetOptions();
            onClose(false);
          } catch (error) {
            enqueueSnackbar(`Something went wrong. ${error?.Detail ? error?.Detail : null}`, {
              variant: 'error'
            });
          }
        }}
      >
        {({ isSubmitting, values, initialValues, handleSubmit }) => {
          return (
            <Modal
              open={open}
              onClose={() => {
                if (!deepEqual(initialValues, values)) {
                  const result = window.confirm(
                    'You have unsaved changes, are you sure you want to exit?'
                  );
                  if (result) {
                    onClose(false);
                  } else {
                    return;
                  }
                } else {
                  onClose(false);
                }
              }}
              maxWidth="md"
            >
              {(isSubmitting || modalIsLoading) && <Loader type="overlay" position="centered" />}
              {budgetOptions && budgetOptions.length > 0 && (
                <Fade in={open}>
                  <Form onSubmit={handleSubmit} autoComplete="none">
                    <Typography variant="h5">{'EDIT BUDGET OPTIONS'}</Typography>
                    <div className={classes.content}>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <CardMui
                            elevation={0}
                            variant="outlined"
                            className={classes.marginBottom}
                          >
                            <CardHeader
                              avatar={<List />}
                              title="OPTIONS"
                              className={classes.primaryHeader}
                            />
                            <CardContent className={classes.cardContent}>
                              <Paper elevation={0} square>
                                <div className={classes.column}>
                                  <Grid container spacing={1}>
                                    <Grid item xs={12}>
                                      <Typography variant={'h5'}>
                                        If an option is included, please provide the price.
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                  <Grid container spacing={1}>
                                    {budgetOptions?.map(option => {
                                      return (
                                        <Grid key={option.optionId} item xs={12}>
                                          <BudgetOptionsField
                                            option={option}
                                            selectedOptions={selectedBudgetOptions}
                                            setSelectedOptions={setSelectedBudgetOptions}
                                          />
                                        </Grid>
                                      );
                                    })}
                                  </Grid>
                                </div>
                              </Paper>
                            </CardContent>
                          </CardMui>
                        </Grid>
                      </Grid>
                    </div>
                    <CardActions>
                      <Button
                        disabled={deepEqual(originalSelectedBudgetOptions, selectedBudgetOptions)}
                        type="submit"
                        startIcon={<Save />}
                        variant="contained"
                        color="primary"
                      >
                        Save
                      </Button>
                      <Button
                        type="button"
                        variant="contained"
                        color="inherit"
                        startIcon={<Close />}
                        onClick={() => {
                          if (!deepEqual(originalSelectedBudgetOptions, selectedBudgetOptions)) {
                            const result = window.confirm(
                              'You have unsaved changes, are you sure you want to exit?'
                            );
                            if (result) {
                              onClose(false);
                            } else {
                              return;
                            }
                          } else {
                            onClose(true);
                          }
                        }}
                      >
                        Cancel
                      </Button>
                    </CardActions>
                  </Form>
                </Fade>
              )}
            </Modal>
          );
        }}
      </Formik>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  primaryHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    marginBottom: theme.spacing(1)
  },
  marginBottom: {
    marginBottom: theme.spacing(1)
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    '& > div:not(:first-of-type)': {
      marginTop: theme.spacing(1)
    }
  },
  outlinedLabel: {
    backgroundColor: theme.palette.common.white,
    paddingLeft: 2,
    paddingRight: 2
  },
  content: {
    marginTop: theme.spacing(1)
  },
  cardContent: {
    paddingTop: 0
  },
  paperBorder: {
    border: `1px solid ${theme.palette.grey[300]}`
  }
}));
